import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  TicketIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { Loading } from '../../components/common';
import { getAdminStats } from '../../services/adminService';

const StatCard = ({ title, value, icon: Icon, change, link, color = 'primary' }) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <Link
      to={link}
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {change && (
            <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
              <ArrowTrendingUpIcon className="h-4 w-4" />
              {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Link>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getAdminStats();
        setStats(response.data || {});
      } catch (err) {
        console.error('Failed to fetch admin stats:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of Kwikly platform metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          icon={UsersIcon}
          change={`+${stats?.users?.thisWeek || 0} this week`}
          link="/admin/users"
          color="primary"
        />
        <StatCard
          title="Pro Subscribers"
          value={stats?.users?.pro || 0}
          icon={CurrencyRupeeIcon}
          link="/admin/users?plan=pro"
          color="green"
        />
        <StatCard
          title="Active Coupons"
          value={stats?.coupons?.active || 0}
          icon={TicketIcon}
          change={`${stats?.coupons?.redemptionsThisWeek || 0} redemptions this week`}
          link="/admin/coupons"
          color="blue"
        />
        <StatCard
          title="Affiliates"
          value={stats?.affiliates?.active || 0}
          icon={UserGroupIcon}
          change={`${stats?.affiliates?.pending || 0} pending`}
          link="/admin/affiliates"
          color="purple"
        />
      </div>

      {/* Top Coupons & Recent Redemptions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Coupons */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Top Coupons</h2>
          </div>
          <div className="p-6">
            {stats?.coupons?.top?.length > 0 ? (
              <div className="space-y-4">
                {stats.coupons.top.map((coupon, index) => (
                  <div key={coupon._id || index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{coupon.code}</p>
                      <p className="text-sm text-gray-500">
                        {coupon.type === 'percentage' ? `${coupon.value}% off` :
                         coupon.type === 'flat' ? `₹${coupon.value} off` : coupon.type}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{coupon.usedCount} uses</p>
                      <p className="text-sm text-green-600">₹{coupon.revenue || 0} revenue</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No coupons yet</p>
            )}
            <Link
              to="/admin/coupons"
              className="block text-center text-primary-600 hover:text-primary-700 font-medium mt-4"
            >
              View all coupons
            </Link>
          </div>
        </div>

        {/* Recent Redemptions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Recent Redemptions</h2>
          </div>
          <div className="p-6">
            {stats?.recentRedemptions?.length > 0 ? (
              <div className="space-y-4">
                {stats.recentRedemptions.slice(0, 5).map((redemption, index) => (
                  <div key={redemption._id || index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{redemption.code}</p>
                      <p className="text-sm text-gray-500">
                        {redemption.userId?.email || 'Unknown user'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {new Date(redemption.redeemedAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-green-600">
                        {redemption.discountType === 'percentage'
                          ? `${redemption.discountValue}%`
                          : `₹${redemption.discountValue}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No redemptions yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Affiliates */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Top Affiliates</h2>
          <Link to="/admin/affiliates" className="text-sm text-primary-600 hover:text-primary-700">
            View all
          </Link>
        </div>
        <div className="p-6">
          {stats?.affiliates?.top?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                    <th className="pb-3 font-medium">Affiliate</th>
                    <th className="pb-3 font-medium">Code</th>
                    <th className="pb-3 font-medium text-right">Conversions</th>
                    <th className="pb-3 font-medium text-right">Earnings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.affiliates.top.map((affiliate, index) => (
                    <tr key={affiliate._id || index}>
                      <td className="py-3">
                        <p className="font-medium text-gray-900">
                          {affiliate.companyName || affiliate.userId?.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500">{affiliate.userId?.email}</p>
                      </td>
                      <td className="py-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-mono">
                          {affiliate.couponCode}
                        </span>
                      </td>
                      <td className="py-3 text-right font-medium">
                        {affiliate.totalConversions || 0}
                      </td>
                      <td className="py-3 text-right font-semibold text-green-600">
                        ₹{(affiliate.totalEarnings || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No affiliates yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
